import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { Organization } from "../../../../core/models/entities/Organization";
import { Profile } from "../../../../core/models/entities/Profile";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import FunctionalList from "../../../components/GenericTable/FunctionalList";
import { useFunctionalListLogosViewModel } from "../vmHooks/useFunctionalListLogosViewModel";
import { LogoOptions } from "../../../../core/models/entities/LogoOptions";
import { useLogoActionHandler } from "../hooks/useLogoActionsHandler";
import { useApprovalDialogByRole } from "../../../components/Dialogs/useApprovalDialogByRole";
import { useCreateLogoDialog } from "../../customer-list/hooks/useCreateLogoDialog";
import { FilterChangeEvent } from "../../../components/GenericTable/SubComponents/TableHeader/ListHeader";
import React from "react";
import { useHasChainLogos } from "../hooks/useHasChainLogos";
import { Role } from "../../../../core/models/entities/Role";
import { useTranslation } from "react-i18next";
import { isDanishLanguage } from "../../../../i18n";
import { enviroment } from "../../../../enviroment";
import { useLogoSearchFeedback } from "../hooks/useLogoSearchFeedback";

interface WrapperProps {
  profile: Profile;
  logos?: BaseLogo[];
  isPageLoading: boolean;
  // keepPreviousData indicators from useAllLogos: while isFetching &&
  // isPreviousData the shown rows belong to the previous query and
  // FunctionalList dims them (no full spinner).
  isFetching?: boolean;
  isPreviousData?: boolean;
  // GetAllLogos' totalCount for the search status line (flag
  // logoSearchFeedbackEnabled); undefined = "N on this page" fallback.
  totalCount?: number;
  lastPage: number;
  options: LogoOptions;
  query: PageQuery;
  queryChanger: QueryChanger;
  customers: Organization[];
  vendors?: Organization[];

  onSelectLogo: (logo: BaseLogo) => void;
  onViewRejectNote: (logo: BaseLogo) => void;
  updatingLogoId?: number;
  // Optional paginator hover-prefetch (forwarded to FunctionalList).
  onPrefetchPage?: (pageIndex: number) => void | Promise<void>;
}

const CHAIN_LOGO_CLASSIFICATION = "11";
const NUMBER_LOGO_CLASSIFICATION = "6";
const NAME_LOGO_CLASSIFICATION = "2";

export function LogoListWrapper({
  profile,
  logos,
  isPageLoading,
  isFetching,
  isPreviousData,
  totalCount,
  lastPage,
  options,
  query,
  queryChanger,
  customers,
  vendors,
  onSelectLogo,
  onViewRejectNote,
  updatingLogoId,
  onPrefetchPage,
}: WrapperProps) {
  const { t, i18n } = useTranslation();
  // Owner decision (confirmed 15/7-2026): chain logos (Kædelogoer — logos
  // from Danish sports chains) are shown ONLY while Danish is the selected
  // UI language — but for admins that includes act-as sessions (see
  // useHasChainLogos: an active impersonation is always admin-driven).
  const isDanish = isDanishLanguage(i18n.language);
  const {
    handleSkipApproval,
    handleSkipAddToCart,
    handleCommitAndInitiateApproval,
  } = useLogoActionHandler();
  const { component, start: openInitApproval } =
    useApprovalDialogByRole<BaseLogo>({
      role: profile.userOrganizationInformations.role,
      subject: "logo",
      onSkip: (entity) => handleSkipApproval(entity).then(),
      onOrder: (entity) => handleSkipAddToCart(entity, 10).then(),
      onInitApproval: (entity, email) =>
        handleCommitAndInitiateApproval(entity, {}, { email }).then(),
    });

  const { component: componentCreateLogoDialog, open } = useCreateLogoDialog({
    onContinue: async () => {},
    onCancel: () => {},
    options: options,
  });

  function openCreateLogoDialog() {
    open();
  }

  const classificationFilter = query.filterBy?.find(
    (f) => f.field === "classification"
  );
  const currentClassification = classificationFilter?.value;

  const isVendor = profile.userOrganizationInformations.role >= Role.Vendor;
  // Skip the has-chain-logos lookup entirely on non-Danish UI languages —
  // the tab is never rendered there (see isDanish gate on the button below).
  const hasChainLogos = useHasChainLogos(
    profile.userOrganizationInformations.role,
    isVendor && isDanish,
    profile.userOrganizationInformations.organizationId
  );

  const shouldShowToggle =
    profile.detailedConfigurations.organizationCountry === "DK";

  const handleClassificationChange = (classification: string | null) => {
    const newFilters =
      query.filterBy?.filter((f) => f.field !== "classification") || [];

    if (classification) {
      newFilters.push({
        field: "classification",
        value: classification,
      });
    }

    queryChanger.handleFilterChange({
      activeFilters: newFilters.map((f) => ({
        key: f.field,
        value: f.value,
      })),
    } as FilterChangeEvent);
  };

  // The chain-logos filter can be active via a deep link (?classification=11
  // in the URL) or because the user switched language while on the tab. On
  // non-Danish languages the tab is hidden, so reset the filter back to all
  // logos instead of leaving the list stuck on an invisible selection.
  React.useEffect(() => {
    if (!isDanish && currentClassification === CHAIN_LOGO_CLASSIFICATION) {
      handleClassificationChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDanish, currentClassification]);

  const vm = useFunctionalListLogosViewModel(
    profile,
    isPageLoading,
    lastPage,
    options,
    query,
    queryChanger,
    onSelectLogo,
    onViewRejectNote,
    openInitApproval,
    openCreateLogoDialog,
    customers,
    vendors,
    logos,
    updatingLogoId,
    isFetching,
    isPreviousData
  );

  // Logo Type Toggle "Pill" Component
  const LogoTypeToggle = () => (
    <div className="flex items-center ml-4">
      <div className="flex bg-gray-100 rounded-full p-1 border border-borderGray">
        <button
          onClick={() => handleClassificationChange(null)}
          className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap transition-all ${
            !currentClassification
              ? "bg-white text-primary-600 border border-borderGray"
              : "text-primary-700"
          }`}
        >
          {t("logoList.allLogos")}
        </button>
        {isDanish && hasChainLogos && (
          <button
            onClick={() =>
              handleClassificationChange(CHAIN_LOGO_CLASSIFICATION)
            }
            className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap transition-all ${
              currentClassification === CHAIN_LOGO_CLASSIFICATION
                ? "bg-white text-primary-600 border border-borderGray"
                : "text-primary-700"
            }`}
          >
            {t("logoList.chainLogos")}
          </button>
        )}
        <button
          onClick={() => handleClassificationChange(NUMBER_LOGO_CLASSIFICATION)}
          className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap transition-all ${
            currentClassification === NUMBER_LOGO_CLASSIFICATION
              ? "bg-white text-primary-600 border border-borderGray"
              : "text-primary-700"
          }`}
        >
          {t("logoList.numbersets")}
        </button>
        <button
          onClick={() => handleClassificationChange(NAME_LOGO_CLASSIFICATION)}
          className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap transition-all ${
            currentClassification === NAME_LOGO_CLASSIFICATION
              ? "bg-white text-primary-600 border border-borderGray"
              : "text-primary-700"
          }`}
        >
          {t("logoList.names")}
        </button>
      </div>
    </div>
  );

  // Combine title with toggle
  const tableTitle = (
    <div className="flex items-center font-bold whitespace-nowrap">
      {typeof vm.tableTitle === "string"
        ? vm.tableTitle.toUpperCase()
        : vm.tableTitle}
      {shouldShowToggle && <LogoTypeToggle />}
    </div>
  );

  // Search feedback (flag): spinner in the search field, status line and
  // progress bar - derived from the list's own react-query state, 0 calls.
  // Flag off = {} spread below, so FunctionalList sees exactly today's props.
  const searchFeedback = useLogoSearchFeedback({
    enabled: enviroment.logoSearchFeedbackEnabled,
    searchTerm: query.searchTerm,
    isFetching: !!isFetching,
    isPreviousData: !!isPreviousData,
    totalCount,
    pageResultCount: logos?.length ?? 0,
    onClear: () => queryChanger.handleSearchQueryChange({ searchTerm: "" }),
  });

  // folderBar is not part of TableData - it renders ABOVE the list box
  // (breadcrumb + folder cards; vendor/admin only, see the view model).
  const { folderBar, ...tableData } = vm;

  const vmWithToggle = {
    ...tableData,
    tableTitle: tableTitle,
    center: tableData.center, // Restore original center (likely empty now)
  };

  return (
    <>
      {componentCreateLogoDialog}
      {component}
      {folderBar}
      <FunctionalList<BaseLogo>
        {...vmWithToggle}
        onPrefetchPage={onPrefetchPage}
        {...searchFeedback}
      />
    </>
  );
}
